import { useEffect } from "react";
import { useSpreadsStore } from "@/stores/spreads";
import type { Spread } from "@/types/spread";

type WsMsg =
  | { op: "snapshot"; rows: Spread[] }
  | { op: "update"; updated: Spread[]; deleted: string[] };

/**
 * Subscribe to /ws/spreads and pipe the latest snapshot per animation frame
 * into the store.
 *
 * Mounted ONCE app-wide from ``TopBar`` (which lives in the persistent
 * ``Layout``, present on every route) — NOT per-page. Previously the Spreads
 * page owned this hook, so navigating away closed the WS, flipped
 * ``connected`` → false and froze ``lastSnapshotAt``, which made the global
 * ``LiveRibbon`` read a misleading "OFFLINE … last update 17h ago". A single
 * app-level mount keeps the ribbon honest everywhere; the Spreads page just
 * reads the store.
 *
 * Inline implementation (no wrapper class) because it's trivial and easier
 * to audit. Buffer the latest snapshot; apply once per rAF — any earlier
 * snapshots within the same frame are superseded.
 */
export function useSpreadStream(): void {
  const applySnapshot = useSpreadsStore((s) => s.applySnapshot);
  const applyDelta = useSpreadsStore((s) => s.applyDelta);
  const setConnected = useSpreadsStore((s) => s.setConnected);

  useEffect(() => {
    const url = buildWsUrl("/ws/spreads");
    let closed = false;
    let ws: WebSocket | null = null;
    let pending: WsMsg | null = null;
    let timerId: number | null = null;
    let reconnectTimer: number | null = null;
    // Exponential backoff for reconnects (was fixed 1500ms before).
    // A fixed interval hammered the backend for the full duration of
    // any prolonged outage; this keeps the first retry snappy but
    // caps at 30s so a dead server doesn't attract a ~40 req/min DoS
    // from every open dashboard tab.
    let reconnectDelay = 1000;
    const MAX_RECONNECT_DELAY = 30_000;

    // setTimeout, not requestAnimationFrame: browsers throttle rAF to ~0Hz
    // in unfocused/backgrounded tabs (Chromium freezes rAF entirely), which
    // starved applySnapshot and left the UI showing 0 rows despite WS
    // messages arriving. setTimeout keeps firing (throttled to ~1Hz in
    // background, full-speed when visible).
    const flush = () => {
      timerId = null;
      const msg = pending;
      pending = null;
      if (!msg) return;
      if (msg.op === "snapshot") applySnapshot(msg.rows);
      else if (msg.op === "update") applyDelta(msg.updated, msg.deleted);
    };

    const schedule = () => {
      if (timerId != null) return;
      timerId = window.setTimeout(flush, 16);
    };

    const connect = () => {
      ws = new WebSocket(url);
      // #83: the snapshot arrives as a gzip ArrayBuffer (pre-compressed once
      // server-side); deltas stay as text frames.
      ws.binaryType = "arraybuffer";
      ws.onopen = () => {
        // Successful handshake resets backoff — a clean reconnect
        // after a brief hiccup shouldn't start the next retry at 30s.
        reconnectDelay = 1000;
        setConnected(true);
      };
      ws.onmessage = (ev: MessageEvent) => {
        if (ev.data instanceof ArrayBuffer) {
          // Binary = pre-gzipped snapshot. Inflate off the main parse path.
          void (async () => {
            try {
              const ds = new DecompressionStream("gzip");
              const text = await new Response(
                new Blob([ev.data]).stream().pipeThrough(ds),
              ).text();
              pending = JSON.parse(text) as WsMsg;
              schedule();
            } catch {
              /* ignore */
            }
          })();
          return;
        }
        try {
          if (typeof ev.data !== "string") return;
          pending = JSON.parse(ev.data) as WsMsg;
          schedule();
        } catch {
          /* ignore */
        }
      };
      ws.onerror = () => setConnected(false);
      ws.onclose = () => {
        setConnected(false);
        ws = null;
        if (!closed) {
          reconnectTimer = window.setTimeout(connect, reconnectDelay);
          reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
        }
      };
    };

    connect();

    return () => {
      closed = true;
      if (timerId != null) clearTimeout(timerId);
      if (reconnectTimer != null) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [applySnapshot, applyDelta, setConnected]);
}

function buildWsUrl(path: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}${path}`;
}
